import { useCallback, useEffect, useMemo, useState } from "react";
import { PageGuide } from "@/components/PageGuide";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAccent } from "@/context/AccentContext";
import { Button } from "@/components/ui/button";
import { PhraseHistory } from "@/components/PhraseHistory";
import { LanguageSelect } from "@/components/LanguageSelect";
import { LetterFolder } from "@/components/LetterFolder";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Plus, Languages, Copy, Share2, Trophy, CalendarCheck, Trash2, FileUp, FileDown, Pencil, X, Reply, Loader2, Flame, LifeBuoy, Volume2, Pin, PinOff, CheckCircle2, FolderPlus, Lightbulb, ArrowRight, ArrowLeftRight } from "lucide-react";
import { Flag } from "@/components/Flag";
import { POPULAR_PROMPTS, PROMPT_BY_ID } from "@/lib/prompts";
import { PromptDrawer, PromptIcon } from "@/components/PromptDrawer";
import { PromptRecap } from "@/components/PromptRecap";
import { toast } from "sonner";
import { PACK_TILES, LETTER_TEMPLATES, pkey, STATE_STYLE } from "@/components/tools/phrasebookData";
import { MasteryRing } from "@/components/tools/MasteryRing";
import { ReplyNotes } from "@/components/tools/ReplyNotes";
import { LetterTemplates } from "@/components/tools/LetterTemplates";

export default function Phrasebook() {
  const { languages, lang } = useI18n();
  const { accent } = useAccent();
  const [text, setText] = useState("");
  const [target, setTarget] = useState("en");
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [qIndex, setQIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [packs, setPacks] = useState([]);
  const [openPack, setOpenPack] = useState(null);
  const [packBusy, setPackBusy] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [pinned, setPinned] = useState([]);
  const [drill, setDrill] = useState(null);
  const [progress, setProgress] = useState(null);
  const [packProgress, setPackProgress] = useState({});
  const [promptPins, setPromptPins] = useState([]);
  const [promptRecent, setPromptRecent] = useState([]);
  const [customPrompts, setCustomPrompts] = useState([]);
  const [promptUsage, setPromptUsage] = useState({});
  const [history, setHistory] = useState(null);
  const [weekPack, setWeekPack] = useState(null);
  const [variant, setVariant] = useState(0);
  const [variantCount, setVariantCount] = useState(3);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [transTarget, setTransTarget] = useState(target);
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [tone, setTone] = useState("neutral");
  const [suggestLang, setSuggestLang] = useState(target);
  const [sideBySide, setSideBySide] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [myTemplates, setMyTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState("");
  const [signature, setSignature] = useState("");
  const [review, setReview] = useState(null);
  const [notes, setNotes] = useState(() => localStorage.getItem("cml_reply_notes") || "");
  const [noteIdeas, setNoteIdeas] = useState([]);
  const [notesBusy, setNotesBusy] = useState(false);
  const [letterRefresh, setLetterRefresh] = useState(0);
  const [activeLetter, setActiveLetter] = useState(null);
  const [savingLetter, setSavingLetter] = useState(false);

  useEffect(() => {
    localStorage.setItem("cml_reply_notes", notes);
  }, [notes]);

  const loadStats = useCallback(() => api.get("/phrasebook/stats").then(({ data }) => setStats(data)), []);
  const promptById = useMemo(
    () => ({ ...PROMPT_BY_ID, ...Object.fromEntries(customPrompts.map((p) => [p.id, p])) }),
    [customPrompts]
  );

  const recentPrompts = useMemo(
    () => promptRecent.map((id) => promptById[id]).filter(Boolean).slice(0, 6),
    [promptRecent, promptById]
  );

  const boardPrompts = useMemo(() => {
    const pinned = promptPins.map((id) => promptById[id]).filter(Boolean);
    const rest = POPULAR_PROMPTS.filter((p) => !promptPins.includes(p.id));
    return [...pinned, ...rest];
  }, [promptPins, promptById]);

  const loadPromptPins = () =>
    api
      .get("/prompts/state")
      .then(({ data }) => {
        setPromptPins((data.pinned || []).filter((k) => k.startsWith("tool:")).map((k) => k.slice(5)));
        setPromptUsage(data.usage || {});
        setPromptRecent(
          (data.recent || [])
            .filter((r) => r.prompt_key.startsWith("tool:"))
            .sort((a, b) => String(b.last_opened).localeCompare(String(a.last_opened)))
            .map((r) => r.prompt_key.slice(5))
        );
      })
      .catch((e) => console.error("could not load pinned prompts", e));

  const togglePromptPin = async (id) => {
    const pinned = !promptPins.includes(id);
    setPromptPins((list) => (pinned ? [...list, id] : list.filter((x) => x !== id)));
    try {
      await api.post("/prompts/pin", { prompt_key: `tool:${id}`, pinned });
      const name = promptById[id]?.label || "That prompt";
      toast.success(pinned ? `${name} pinned to the top` : `${name} unpinned`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not pin that prompt");
      loadPromptPins();
    }
  };

  const loadProgress = useCallback(() => api.get("/phrasebook/progress").then(({ data }) => setProgress(data)), []);
  const loadPackProgress = (lang = target) =>
    api
      .get("/phrasebook/pack-progress", { params: { lang } })
      .then(({ data }) => setPackProgress(data.packs || {}))
      .catch((e) => console.error("could not load pack progress", e));
  const saveAllSuggestions = async () => {
    if (!suggestions.length || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/phrasebook/bulk", {
        language: suggestLang,
        pack_id: "suggested",
        items: suggestions.map((s) => ({ text: s.text, translation: s.translation || "" })),
      });
      toast.success(
        `${data.saved} phrases saved${data.skipped ? ` · ${data.skipped} already there` : ""}`);
      loadHistory();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save those");
    } finally {
      setBusy(false);
    }
  };

  const sharePack = async (pack) => {
    let url;
    try {
      const { data } = await api.post("/packs/share", { pack_id: pack.id, lang: target });
      url = `${window.location.origin}/api/share/pk/${data.share_id}`;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not create a share link");
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({ title: pack.title, text: pack.blurb, url });
        return;
      } catch {
        /* dismissed — fall back to copying */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Pack link copied — send it to a friend");
    } catch {
      toast.message(url);
    }
  };

  const copyPhrase = async (r) => {
    const body = r.translation ? `${r.text} — ${r.translation}` : r.text;
    try {
      await navigator.clipboard.writeText(body);
      toast.success("Copied");
    } catch {
      toast.error("Your browser blocked the copy");
    }
  };

  const sharePhrase = async (r) => {
    let url;
    try {
      const { data } = await api.post("/phrases/share", {
        text: r.text,
        translation: r.translation || "",
        language: r.language || target,
      });
      url = `${window.location.origin}/api/share/ph/${data.share_id}`;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not create a share link");
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({ title: r.text, text: r.translation || r.text, url });
        return;
      } catch {
        /* dismissed — fall back to copying */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — send it to a friend");
    } catch {
      toast.message(url);
    }
  };

  // Long letters are kept only when they still fit a shareable phrase.
  const keepTranslation = async (out, source, lang) => {
    if (out.length > 4000) return;
    try {
      await api.post("/phrasebook", {
        text: out, language: lang, translation: source,
        pack_id: "translations", source: "translation",
      });
      loadHistory();
    } catch (e) {
      console.error("could not keep that translation", e);
    }
  };

  const loadSignature = () =>
    api.get("/phrasebook/signature").then(({ data }) => setSignature(data.text || ""));

  const saveSignature = async () => {
    const { data } = await api.put("/phrasebook/signature", { text: signature });
    setSignature(data.text);
    toast.success(data.text ? "Signature saved" : "Signature cleared");
  };

  const draftReply = async () => {
    const body = text.trim();
    if (!body || translating) return;
    setTranslating(true);
    try {
      const { data } = await api.post("/translate/reply", {
        text: body, target: transTarget, tone, signature: true, note: notes.trim(),
      });
      setTranslation({ out: data.translation, source_text: data.draft_en, target: transTarget });
      toast.success("MiLu drafted a reply — tweak it with Edit if you like");
      await keepTranslation(data.translation, data.draft_en, transTarget);
      if (activeLetter) {
        try {
          await api.post(`/letters/${activeLetter}/replies`, {
            draft_en: data.draft_en, translation: data.translation,
            target: transTarget, tone, notes: notes.trim(),
          });
          setLetterRefresh((n) => n + 1);
          toast.message("Reply filed with that letter");
        } catch (e) {
          console.error("could not file the reply", e);
        }
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not draft that reply");
    } finally {
      setTranslating(false);
    }
  };

  const suggestNotes = async () => {
    const body = text.trim();
    if (!body || notesBusy) {
      if (!body) toast.error("Paste the letter first and MiLu will suggest what to say");
      return;
    }
    setNotesBusy(true);
    try {
      const { data } = await api.post("/translate/reply/notes", { text: body, tone, language: lang });
      setNoteIdeas(data.ideas || []);
      if (!data.ideas?.length) toast.info("MiLu could not think of anything for that one");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not think of answers");
    } finally {
      setNotesBusy(false);
    }
  };

  const addNote = (idea) =>
    setNotes((n) => (n.trim() ? `${n.replace(/\s+$/, "")}\n• ${idea}` : `• ${idea}`));

  const saveLetter = async () => {
    if (!translation || savingLetter) return;
    setSavingLetter(true);
    try {
      const { data } = await api.post("/letters", {
        title: translation.filename || "",
        source_text: translation.source_text || translation.out,
        translation: translation.out,
        target: translation.target,
        tone,
        notes: notes.trim(),
        filename: translation.filename || "",
      });
      setActiveLetter(data.letter_id);
      setLetterRefresh((n) => n + 1);
      toast.success(
        data.auto_label
          ? `Kept in My letters under “${data.auto_label}” — replies you draft now go with it`
          : "Kept in My letters — replies you draft now go with it"
      );
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that letter");
    } finally {
      setSavingLetter(false);
    }
  };

  const openLetter = (doc) => {
    setText(doc.source_text || "");
    setTransTarget(doc.target || "en");
    setTone(doc.tone || "neutral");
    setNotes(doc.notes || "");
    setNoteIdeas([]);
    setActiveLetter(doc.letter_id);
    const last = (doc.replies || [])[doc.replies.length - 1];
    setTranslation(
      last
        ? { out: last.translation, source_text: last.draft_en, target: last.target }
        : { out: doc.translation, source_text: doc.source_text, target: doc.target }
    );
    setEditing(false);
    setSideBySide(false);
    document.querySelector('[data-testid="phrase-input"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const loadTemplates = () => api.get("/phrasebook/templates").then(({ data }) => setMyTemplates(data));

  const saveTemplate = async () => {
    const line = newTemplate.trim();
    if (!line) return;
    try {
      await api.post("/phrasebook/templates", { text: line });
      setNewTemplate("");
      loadTemplates();
      toast.success("Line saved — it will be here every time");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that line");
    }
  };

  const removeTemplate = async (t) => {
    await api.delete(`/phrasebook/templates/${t.template_id}`);
    loadTemplates();
  };

  const insertTemplate = (line) => {
    setText((t) => (t.trim() ? `${t.replace(/\s+$/, "")}\n${line}` : line));
  };

  const finishEditing = async () => {
    setEditing(false);
    if (translation?.out?.trim()) await keepTranslation(translation.out, translation.source_text, translation.target);
  };

  const downloadTranslation = async (fmt) => {
    if (!translation || exporting) return;
    setExporting(fmt);
    try {
      const res = await api.post(
        "/translate/export",
        {
          translation: translation.out,
          source_text: translation.source_text,
          language: translation.target,
          filename: translation.filename || "translation",
          include_original: true,
          fmt,
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(translation.filename || "translation").replace(/\.[^.]+$/, "")}-${translation.target}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = "Could not build that file";
      if (e.response?.data instanceof Blob) {
        try {
          detail = JSON.parse(await e.response.data.text()).detail || detail;
        } catch {
          /* keep the generic message */
        }
      }
      toast.error(detail);
    } finally {
      setExporting(null);
    }
  };

  const translateFile = async (file) => {
    if (!file || translating) return;
    setTranslating(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("target", transTarget);
      form.append("tone", tone);
      const { data } = await api.post("/translate/document", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTranslation({
        out: data.translation, source_text: data.source_text, target: transTarget,
        filename: data.filename, clipped: data.clipped,
      });
      setActiveLetter(null);
      if (data.clipped) toast.message("That file was long — I translated the first 12,000 characters");
      await keepTranslation(data.translation, data.source_text, transTarget);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not translate that file");
    } finally {
      setTranslating(false);
    }
  };

  const translateText = async (rawText, rawTarget) => {
    const body = (typeof rawText === "string" ? rawText : text).trim();
    const dest = typeof rawTarget === "string" ? rawTarget : transTarget;
    if (!body || translating) return;
    setTranslating(true);
    try {
      const { data } = await api.post("/translate", { text: body, target: dest, tone });
      setTranslation({ out: data.translation, source_text: body, target: dest,
                       detected: data.detected || "" });
      setActiveLetter(null);
      await keepTranslation(data.translation, body, dest);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not translate that");
    } finally {
      setTranslating(false);
    }
  };

  // One tap: send the translation back the other way, into the language I typed in.
  const swapDirection = () => {
    const back = translation?.detected;
    if (!back || translating) return;
    setText(translation.out);
    setTransTarget(back);
    translateText(translation.out, back);
  };

  const orderedPacks = useMemo(
    () => [...packs].sort((a, b) => (pinned.includes(b.id) ? 1 : 0) - (pinned.includes(a.id) ? 1 : 0)),
    [packs, pinned]
  );

  const loadWeekPack = async (lang = target) => {
    try {
      const { data } = await api.get("/phrasebook/pack-of-week", { params: { lang } });
      setWeekPack(data);
      if (data.just_completed) {
        toast.success(`You mastered ${data.pack.title} — MiLu is proud of you!`);
      }
    } catch (e) {
      console.error("could not load the pack of the week", e);
    }
  };

  const practiseWeekPack = async () => {
    let list = packs;
    if (!list.length) list = await loadSurvival(target);
    const pack = (list || []).find((p) => p.id === weekPack.pack.id);
    if (!pack) {
      toast.error("Load the survival packs first");
      return;
    }
    setOpenPack(pack.id);
    startDrill(pack);
    document.querySelector(`[data-testid="survival-pack-${pack.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const jumpToPack = async (id) => {
    let list = packs;
    if (!list.length) list = await loadSurvival(target);
    if (!(list || []).some((p) => p.id === id)) {
      toast.error("Could not open that pack — try Refresh list");
      return;
    }
    setOpenPack(id);
    setTimeout(
      () =>
        document
          .querySelector(`[data-testid="survival-pack-${id}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      80
    );
  };

  const loadHistory = useCallback(() => api.get("/phrasebook/history").then(({ data }) => setHistory(data)), []);
  useEffect(() => {
    loadStats();
    loadProgress();
    loadHistory();
    loadWeekPack();
    loadPackProgress();
    loadPromptPins();
    api.get("/prompts/custom").then(({ data }) => setCustomPrompts(data.prompts || [])).catch(() => {});
    loadTemplates();
    loadSignature();
    // mount only: these loaders read the current language from state on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recordReview = (item, known) =>
    api
      .post("/phrasebook/review", {
        text: item.text,
        translation: item.translation || item.en || "",
        language: item.language || target,
        pack_id: item.pack_id || "",
        known,
      })
      .catch((e) => console.error("could not save review progress", e));

  const startReview = () => {
    if (!progress?.due?.length) return;
    setReview({ queue: progress.due, at: 0, revealed: false, right: 0 });
  };

  const gradeReview = async (known) => {
    const item = review.queue[review.at];
    await recordReview(item, known);
    if (review.at + 1 >= review.queue.length) {
      const right = review.right + (known ? 1 : 0);
      setReview(null);
      await loadProgress();
      loadHistory();
      loadWeekPack();
      loadPackProgress();
      toast.success(`Review done — ${right}/${review.queue.length} remembered`);
      return;
    }
    setReview((r) => ({ ...r, at: r.at + 1, revealed: false, right: r.right + (known ? 1 : 0) }));
  };

  const packMastery = (pack) => {
    const states = progress?.states || {};
    const done = pack.phrases.filter((p) => states[pkey(target, p.text)]?.state === "mastered").length;
    return { done, total: pack.phrases.length };
  };

  const startQuiz = async () => {
    try {
      const { data } = await api.get("/phrasebook/quiz");
      setQuiz(data);
      setQIndex(0);
      setPicked(null);
      setScore(0);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const answer = (option) => {
    if (picked) return;
    setPicked(option);
    const q = quiz.questions[qIndex];
    const correct = option === q.answer;
    if (correct) setScore((s) => s + 1);
    recordReview({ text: q.answer, translation: q.prompt, language: q.language, pack_id: "saved" }, correct);
    setTimeout(async () => {
      if (qIndex + 1 < quiz.questions.length) {
        setQIndex((i) => i + 1);
        setPicked(null);
      } else {
        const total = quiz.questions.length;
        const finalScore = score + (correct ? 1 : 0);
        const { data } = await api.post("/phrasebook/quiz/result", { correct: finalScore, total });
        setStats({ ...data, practised_today: true });
        setQuiz(null);
        loadProgress();
        loadHistory();
        toast.success(`${finalScore}/${total} correct · ${data.streak}-day streak`);
      }
    }, 750);
  };

  const add = async (payload) => {
    try {
      await api.post("/phrasebook", payload);
      await loadHistory();
      toast.success("Saved to your phrasebook");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const speak = async (text, slow = false, meta = {}) => {
    const key = slow ? `slow:${text}` : text;
    if (playing === key) return;
    setPlaying(key);
    try {
      const res = await api.get("/phrasebook/speech", {
        params: { text, slow },
        responseType: "blob",
      });
      const audio = new Audio(URL.createObjectURL(res.data));
      audio.onended = () => setPlaying(null);
      audio.onerror = () => setPlaying(null);
      await audio.play();
      api
        .post("/phrasebook/touch", {
          text,
          translation: meta.translation || "",
          phonetic: meta.phonetic || "",
          language: meta.language || target,
          pack_id: meta.pack_id || "",
          action: "played",
        })
        .then(loadHistory)
        .catch(() => {});
    } catch (e) {
      toast.error("Could not read that one out loud");
      setPlaying(null);
    }
  };

  const loadSurvival = async (lang = target, set = 0) => {
    setPackBusy("load");
    try {
      const { data } = await api.get("/phrasebook/survival", { params: { lang, variant: set } });
      setPacks(data.packs);
      setPinned(data.pinned || []);
      setOpenPack(data.packs[0]?.id || null);
      setVariant(data.variant);
      setVariantCount(data.variant_count || 3);
      setHiddenCount(data.hidden_count || 0);
      return data.packs;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      return [];
    } finally {
      setPackBusy(null);
    }
  };

  const refreshList = () => {
    if (!packs.length) return loadSurvival(target, 0);
    const next = (variant + 1) % variantCount;
    toast.message(`Bringing you set ${next + 1} — MiLu may need a moment to write new lines`);
    return loadSurvival(target, next);
  };

  const hidePhrase = async (line) => {
    setPacks((list) =>
      list.map((p) => ({ ...p, phrases: p.phrases.filter((x) => x.text !== line.text) })));
    setHiddenCount((n) => n + 1);
    try {
      await api.post("/phrasebook/hide", { text: line.text, language: target });
    } catch {
      toast.error("Could not remove that line");
      loadSurvival(target, variant);
    }
  };

  const restoreHidden = async () => {
    const { data } = await api.post("/phrasebook/unhide");
    toast.success(`${data.restored} line${data.restored === 1 ? "" : "s"} back on the list`);
    loadSurvival(target, variant);
  };

  const forgetPhrase = async (r) => {
    try {
      await api.post("/phrasebook/forget", null, { params: { key: r.key } });
      loadHistory();
    } catch {
      toast.error("Could not remove that from your history");
    }
  };

  const togglePin = async (pack) => {
    try {
      const { data } = await api.post("/phrasebook/survival/pin", { pack_id: pack.id });
      setPinned(data.pinned);
      toast.success(data.pinned.includes(pack.id) ? `${pack.title} pinned to the top` : "Unpinned");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const startDrill = (pack) => {
    const order = [...pack.phrases].sort(() => Math.random() - 0.5);
    setOpenPack(pack.id);
    setDrill({ packId: pack.id, queue: order, at: 0, right: 0, picked: null });
  };

  const drillOptions = (pack, answer) => {
    const others = pack.phrases.filter((p) => p.text !== answer.text);
    const picks = others.sort(() => Math.random() - 0.5).slice(0, 3);
    return [answer, ...picks].sort(() => Math.random() - 0.5);
  };

  const answerDrill = (pack, choice) => {
    const current = drill.queue[drill.at];
    const right = choice.text === current.text;
    recordReview({ ...current, translation: current.en, language: target, pack_id: pack.id }, right);
    setDrill((d) => ({ ...d, picked: choice.text, right: d.right + (right ? 1 : 0) }));
    setTimeout(() => {
      setDrill((d) => {
        if (!d) return d;
        if (d.at + 1 >= d.queue.length) {
          toast.success(`Pack finished — ${d.right}/${d.queue.length} right`);
          loadProgress();
          loadHistory();
          loadWeekPack();
          loadPackProgress();
          return null;
        }
        return { ...d, at: d.at + 1, picked: null };
      });
    }, 900);
  };

  const savePack = async (pack) => {
    setPackBusy(pack.id);
    try {
      const { data } = await api.post("/phrasebook/survival/save", { lang: target, pack_id: pack.id, variant });
      await loadHistory();
      toast.success(
        data.saved
          ? `${data.saved} phrases saved${data.skipped ? ` · ${data.skipped} already there` : ""}`
          : "You already have all of these",
      );
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setPackBusy(null);
    }
  };

  const suggest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/phrasebook/suggest", { target_language: suggestLang });
      setSuggestions(data.suggestions);
      if (!data.suggestions.length) toast.info("Chat a little more and I'll have better ideas");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cml-theme mx-auto max-w-6xl px-5 py-12 sm:px-8" style={{ "--theme": accent }}
         data-testid="phrasebook-page">
      <p className="text-xs font-semibold uppercase tracking-widest text-brand">Smart Tools</p>
      <h1 className="mt-3 font-display text-[1.7rem] font-extrabold tracking-tighter sm:text-[2.25rem]">
        Phrases Worth Keeping
      </h1>
      <p className="mt-3 max-w-xl text-sm text-ink-soft md:text-base">
        Save any message or caption with one tap, or let ChatMiLu suggest what to learn next from your real
        conversations.
      </p>

      <div className="mt-10 grid gap-6 lg:grid-cols-2 lg:gap-8">
        {weekPack && (
          <div
            data-testid="pack-of-week"
            className={`rounded-3xl border p-7 lg:col-span-2 ${
              weekPack.complete ? "border-forest/40 bg-forest/10" : "border-brand/25 bg-panel"
            }`}
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand">
                {weekPack.complete ? (
                  <Trophy className="h-3.5 w-3.5 text-forest" />
                ) : (
                  <CalendarCheck className="h-3.5 w-3.5" />
                )}
                Pack of the week
              </p>
              <p className="text-xs text-ink-soft">{weekPack.week}</p>
              {!weekPack.complete && (
                <Button
                  data-testid="practise-week-pack-button"
                  onClick={practiseWeekPack}
                  className="ms-auto min-h-[44px] rounded-full bg-brand px-6 hover:bg-brand-hover active:scale-95"
                >
                  Practise this pack
                </Button>
              )}
            </div>

            <p className="mt-3 font-display text-2xl font-extrabold tracking-tight" data-testid="week-pack-title">
              Survival Phrases
            </p>
            <p className="mt-1 text-sm text-ink-soft">
              Eight packs of the lines that get you through the day — tap one to open it.
            </p>

            <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8" data-testid="pack-tiles">
              {PACK_TILES.map(([id, label, tile], index) => {
                const p = packProgress[id] || { mastered: 0, total: 0, percent: 0 };
                const dash = 2 * Math.PI * 15;
                const origin = index === 0 ? "origin-left"
                  : index === PACK_TILES.length - 1 ? "origin-right" : "origin-center";
                return (
                  <button
                    key={id}
                    data-testid={`pack-tile-${id}`}
                    onClick={() => jumpToPack(id)}
                    className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-page text-start transition-transform duration-200 ease-out hover:z-20 hover:scale-[1.9] hover:border-brand/60 hover:shadow-2xl hover:shadow-black/60 focus-visible:z-20 focus-visible:scale-[1.9] active:scale-[0.98] ${origin}`}
                  >
                    <span className="relative block">
                      <img
                        src={tile}
                        alt=""
                        loading="lazy"
                        className="aspect-[5/4] w-full object-cover"
                      />
                      <span
                        data-testid={`pack-tile-ring-${id}`}
                        title={`${p.mastered} of ${p.total} mastered`}
                        className="absolute start-1.5 top-1.5 grid h-9 w-9 place-items-center rounded-full bg-black/55 backdrop-blur-sm"
                      >
                        <svg viewBox="0 0 36 36" className="absolute h-9 w-9 -rotate-90">
                          <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="3" />
                          <circle
                            cx="18"
                            cy="18"
                            r="15"
                            fill="none"
                            stroke={p.percent >= 100 ? "var(--forest, #2f9e6b)" : "#2f7bff"}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeDasharray={dash}
                            strokeDashoffset={dash * (1 - Math.min(p.percent, 100) / 100)}
                            style={{ transition: "stroke-dashoffset 600ms ease" }}
                          />
                        </svg>
                        {p.percent >= 100 ? (
                          <CheckCircle2 className="relative h-3.5 w-3.5 text-forest" />
                        ) : (
                          <span className="relative text-[9px] font-bold tabular-nums">{p.percent}%</span>
                        )}
                      </span>
                      {weekPack.pack.id === id && (
                        <span className="absolute end-1.5 top-1.5 rounded-full bg-brand px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-white">
                          This week
                        </span>
                      )}
                    </span>
                    <span className="line-clamp-2 block min-h-[26px] px-2.5 pt-2 text-[11px] font-semibold leading-tight">{label}</span>
                    <span className="block px-2.5 pb-2 pt-0.5 text-[10px] text-ink-soft" data-testid={`pack-tile-count-${id}`}>
                      {p.total ? `${p.mastered} of ${p.total}` : "none yet"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="rounded-3xl border border-white/10 bg-panel p-7 lg:col-span-2" data-testid="prompts-card">
          <div className="flex flex-wrap items-center gap-4">
            <p className="inline-flex items-center gap-2 font-display text-lg font-bold tracking-tight">
              <Sparkles className="h-4 w-4 text-brand" /> Popular Prompts
            </p>
            <p className="text-sm text-ink-soft">Tap one and MiLu opens the chat with it ready to go</p>
            <PromptDrawer pins={promptPins} custom={customPrompts} onTogglePin={togglePromptPin} usage={promptUsage} />
            {progress?.due_count > 0 && !review && (
              <Button
                data-testid="start-review-button"
                onClick={startReview}
                className="ms-auto min-h-[44px] rounded-full bg-forest px-6 text-page hover:bg-forest/90 active:scale-95"
              >
                Review {progress.due_count} due {progress.due_count === 1 ? "phrase" : "phrases"}
              </Button>
            )}
          </div>

          <div className="mt-5">
            <PromptRecap custom={customPrompts} />
          </div>

          {recentPrompts.length > 0 && (
            <div className="mt-5" data-testid="recent-prompts">
              <p className="text-xs font-semibold uppercase tracking-widest text-ink-soft">Recently used</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {recentPrompts.map((p) => (
                  <Link
                    key={p.id}
                    to={`/chat?prompt=${p.id}`}
                    data-testid={`recent-prompt-${p.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-page py-1.5 pe-4 ps-1.5 text-xs font-semibold text-ink-soft transition-colors hover:border-brand/60 hover:text-brand"
                  >
                    {p.tile ? (
                      <img src={p.tile} alt="" loading="lazy" className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <span className="grid h-7 w-7 place-items-center rounded-full border border-white/10">
                        <PromptIcon name={p.icon} className="h-3.5 w-3.5 text-brand" />
                      </span>
                    )}
                    {p.label}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="prompt-tiles">
            {boardPrompts.map((p) => (
              <Link
                key={p.id}
                to={`/chat?prompt=${p.id}`}
                data-testid={`prompt-tile-${p.id}`}
                className={`group flex items-center gap-4 rounded-2xl border bg-page p-3 transition-all hover:-translate-y-0.5 hover:border-brand/60 active:scale-[0.99] ${
                  promptPins.includes(p.id) ? "border-brand/50" : "border-white/10"
                }`}
              >
                {p.tile ? (
                  <img
                    src={p.tile}
                    alt=""
                    loading="lazy"
                    className="h-14 w-14 shrink-0 rounded-xl object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl border border-white/10 bg-panel">
                    <PromptIcon name={p.icon} className="h-6 w-6 text-brand" />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-sm font-bold tracking-tight">{p.label}</span>
                  <span className="mt-0.5 block text-xs leading-snug text-ink-soft">{p.blurb}</span>
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`prompt-tile-pin-${p.id}`}
                  aria-label={promptPins.includes(p.id) ? `Unpin ${p.label}` : `Pin ${p.label}`}
                  title={promptPins.includes(p.id) ? "Unpin from the top" : "Pin to the top"}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    togglePromptPin(p.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      togglePromptPin(p.id);
                    }
                  }}
                  className={`shrink-0 rounded-full border p-1.5 transition-colors ${
                    promptPins.includes(p.id)
                      ? "border-brand bg-brand/15 text-brand"
                      : "border-white/10 text-ink-soft opacity-50 hover:border-brand/50 hover:text-brand group-hover:opacity-100"
                  }`}
                >
                  {promptPins.includes(p.id) ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                </span>
              </Link>
            ))}
          </div>


          {review && (
            <div className="mt-6 rounded-2xl border border-forest/30 bg-page p-5" data-testid="review-panel">
              <p className="text-xs uppercase tracking-widest text-ink-soft">
                {review.at + 1} of {review.queue.length} · {review.right} remembered
              </p>
              <p dir="auto" className="mt-2 font-display text-xl font-bold tracking-tight" data-testid="review-prompt">
                {review.queue[review.at].translation || review.queue[review.at].text}
              </p>
              {!review.revealed ? (
                <Button
                  data-testid="review-reveal-button"
                  onClick={() => setReview((r) => ({ ...r, revealed: true }))}
                  className="mt-4 min-h-[44px] rounded-full bg-brand px-6 hover:bg-brand-hover active:scale-95"
                >
                  Show the answer
                </Button>
              ) : (
                <>
                  <p dir="auto" className="mt-3 flex items-center gap-2 text-lg font-medium" data-testid="review-answer">
                    {review.queue[review.at].text}
                    <button
                      data-testid="review-speak-button"
                      onClick={() => speak(review.queue[review.at].text, false, { translation: review.queue[review.at].translation, language: review.queue[review.at].language, pack_id: review.queue[review.at].pack_id })}
                      aria-label="Hear this phrase"
                      className="rounded-full border border-white/10 p-1.5 text-ink-soft hover:border-brand/50 hover:text-ink"
                    >
                      <Volume2 className="h-3.5 w-3.5" />
                    </button>
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      data-testid="review-known-button"
                      onClick={() => gradeReview(true)}
                      className="min-h-[44px] rounded-full bg-forest px-6 text-page hover:bg-forest/90 active:scale-95"
                    >
                      I knew it
                    </Button>
                    <Button
                      data-testid="review-unknown-button"
                      variant="outline"
                      onClick={() => gradeReview(false)}
                      className="min-h-[44px] rounded-full px-6 active:scale-95"
                    >
                      Not yet
                    </Button>
                    <button
                      data-testid="review-stop-button"
                      onClick={() => setReview(null)}
                      className="ms-auto text-xs text-ink-soft underline decoration-dotted hover:text-ink"
                    >
                      Stop for now
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-white/10 bg-panel p-7 lg:col-span-2" data-testid="practice-card">
          <div className="flex flex-wrap items-center gap-4">
            <p className="inline-flex items-center gap-2 font-display text-lg font-bold tracking-tight">
              <Flame className="h-4 w-4 text-brand" /> Daily practice
            </p>
            {stats && (
              <p className="text-sm text-ink-soft" data-testid="practice-stats">
                {stats.streak}-day streak · best {stats.best_streak} · {stats.correct}/{stats.answered} correct
                {stats.practised_today ? " · done today ✓" : ""}
              </p>
            )}
            {!quiz && (
              <Button
                data-testid="start-quiz-button"
                onClick={startQuiz}
                className="ms-auto min-h-[44px] rounded-full bg-brand px-6 hover:bg-brand-hover active:scale-95"
              >
                Start 5-question quiz
              </Button>
            )}
          </div>

          {quiz && (
            <div className="mt-6" data-testid="quiz-panel">
              <p className="text-xs uppercase tracking-widest text-ink-soft">
                Question {qIndex + 1} of {quiz.questions.length}
              </p>
              <p dir="auto" className="mt-2 font-display text-xl font-bold tracking-tight" data-testid="quiz-prompt">
                {quiz.questions[qIndex].prompt}
              </p>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {quiz.questions[qIndex].options.map((o, i) => {
                  const isAnswer = o === quiz.questions[qIndex].answer;
                  const state = !picked ? "" : isAnswer ? "border-forest bg-forest/15" : picked === o ? "border-destructive bg-destructive/15" : "opacity-50";
                  return (
                    <button
                      key={`${i}-${o}`}
                      data-testid={`quiz-option-${i}`}
                      onClick={() => answer(o)}
                      dir="auto"
                      className={`min-h-[48px] rounded-2xl border border-white/15 bg-page px-4 text-start text-sm transition-colors hover:border-brand ${state}`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-white/10 bg-panel p-7">
          <p className="inline-flex items-center gap-2 font-display text-lg font-bold tracking-tight">
            <Languages className="h-4 w-4 text-brand" /> Translations
          </p>
          <p className="mt-1 text-sm text-ink-soft">
            Type anything, pick the language you want it in, and keep the ones worth remembering.
          </p>
          <div className="mt-4">
            <Textarea
              data-testid="phrase-input" dir="auto" value={text} rows={3}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) translateText();
              }}
              placeholder="Add text here to translate in any language… paste a whole paragraph or letter if you like"
              className="min-h-[92px] w-full resize-y rounded-xl"
            />
            {text.length > 0 && (
              <p className="mt-1 text-xs text-ink-soft" data-testid="translate-char-count">
                {text.length.toLocaleString()} characters · Ctrl/⌘ + Enter to translate
              </p>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <LanguageSelect
              languages={languages}
              value={transTarget}
              onChange={setTransTarget}
              testId="translate-language-select"
            />
            <Button
              data-testid="translate-button"
              onClick={translateText}
              disabled={translating}
              className="min-h-[44px] rounded-full bg-brand px-5 hover:bg-brand-hover active:scale-95"
            >
              {translating && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              Translate
            </Button>
            <Button
              data-testid="draft-reply-button"
              variant="outline"
              onClick={draftReply}
              disabled={translating}
              className="min-h-[44px] rounded-full px-5 active:scale-95"
            >
              <Reply className="me-2 h-4 w-4" /> Draft a reply
            </Button>
          </div>

          <ReplyNotes
            notes={notes}
            onChange={setNotes}
            ideas={noteIdeas}
            busy={notesBusy}
            onSuggest={suggestNotes}
            onUseIdea={addNote}
          />

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-ink-soft">Tone</span>
            {[
              ["neutral", "As written"],
              ["formal", "Polite & formal"],
              ["casual", "Friendly & casual"],
            ].map(([id, label]) => (
              <button
                key={id}
                data-testid={`tone-${id}`}
                onClick={() => setTone(id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  tone === id
                    ? "border-brand bg-brand/15 text-brand"
                    : "border-white/10 text-ink-soft hover:border-brand/40 hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
            <label
              data-testid="translate-file-label"
              className="ms-auto inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
            >
              <FileUp className="h-3.5 w-3.5" /> Translate a file
              <input
                data-testid="translate-file-input"
                type="file"
                accept=".pdf,.docx,.txt,.md,.rtf"
                className="hidden"
                onChange={(e) => {
                  translateFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          <p className="mt-1 text-xs text-ink-soft">
            PDF, Word (.docx) or plain text, up to 8 MB — MiLu reads it and gives the whole thing back.
          </p>

          <button
            data-testid="letter-templates-toggle"
            onClick={() => setShowTemplates((v) => !v)}
            className="mt-3 text-xs text-brand underline decoration-dotted"
          >
            {showTemplates ? "Hide letter templates" : "Letter templates"}
          </button>
          {showTemplates && (
            <LetterTemplates
              signature={signature}
              onSignature={setSignature}
              onSaveSignature={saveSignature}
              onInsert={insertTemplate}
              myTemplates={myTemplates}
              newTemplate={newTemplate}
              onNewTemplate={setNewTemplate}
              onSaveTemplate={saveTemplate}
              onRemoveTemplate={removeTemplate}
            />
          )}

          {translation && (
            <div className="mt-4 rounded-2xl border border-brand/25 bg-page p-4" data-testid="translation-result">
              <p className="text-xs uppercase tracking-widest text-ink-soft">
                {languages.find((l) => l.code === translation.target)?.native || translation.target}
                {translation.filename ? ` · ${translation.filename}` : ""}
                {translation.clipped ? " · first 12,000 characters" : ""}
              </p>
              {translation.detected && (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="translate-direction">
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-panel px-3 py-1.5 text-xs"
                        data-testid="translate-detected">
                    <Flag code={translation.detected} />
                    You typed in{" "}
                    <span className="font-semibold">
                      {languages.find((l) => l.code === translation.detected)?.native || translation.detected}
                    </span>
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-ink-soft rtl:rotate-180" />
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-panel px-3 py-1.5 text-xs">
                    <Flag code={translation.target} />
                    <span className="font-semibold">
                      {languages.find((l) => l.code === translation.target)?.native || translation.target}
                    </span>
                  </span>
                  {translation.detected !== translation.target && (
                    <button
                      data-testid="translate-swap"
                      onClick={swapDirection}
                      disabled={translating}
                      title={`Translate back into ${languages.find((l) => l.code === translation.detected)?.native || translation.detected}`}
                      className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-brand/40 px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand/10 active:scale-95 disabled:opacity-50"
                    >
                      <ArrowLeftRight className="h-3.5 w-3.5" /> Swap
                    </button>
                  )}
                </div>
              )}
              <button
                data-testid="side-by-side-toggle"
                onClick={() => setSideBySide((v) => !v)}
                className="mt-1 text-xs text-brand underline decoration-dotted"
              >
                {sideBySide ? "Show stacked" : "Show side by side"}
              </button>

              {sideBySide ? (
                <div className="cml-scroll mt-2 max-h-80 overflow-y-auto" data-testid="side-by-side-view">
                  <div className="grid grid-cols-2 gap-3 border-b border-white/10 pb-1 text-[11px] uppercase tracking-widest text-ink-soft">
                    <span>Original</span>
                    <span>
                      {languages.find((l) => l.code === translation.target)?.native || translation.target}
                    </span>
                  </div>
                  {(() => {
                    const left = translation.source_text.split(/\n+/).filter(Boolean);
                    const right = translation.out.split(/\n+/).filter(Boolean);
                    const rows = Math.max(left.length, right.length);
                    return Array.from({ length: rows }, (_, i) => (
                      <div
                        key={i}
                        data-testid={`side-row-${i}`}
                        className="grid grid-cols-2 gap-3 border-b border-white/5 py-2 text-sm"
                      >
                        <p dir="auto" className="whitespace-pre-wrap text-ink-soft">{left[i] || ""}</p>
                        <p dir="auto" className="whitespace-pre-wrap">{right[i] || ""}</p>
                      </div>
                    ));
                  })()}
                </div>
              ) : (
                editing ? (
                <Textarea
                  data-testid="edit-translation-input"
                  dir="auto"
                  value={translation.out}
                  rows={6}
                  onChange={(e) => setTranslation((t) => ({ ...t, out: e.target.value }))}
                  className="mt-2 min-h-[140px] w-full resize-y rounded-xl"
                />
              ) : (
                <div className="cml-scroll mt-2 max-h-72 overflow-y-auto">
                  <p
                    dir="auto"
                    className={`whitespace-pre-wrap ${
                      translation.out.length > 180
                        ? "text-base leading-relaxed"
                        : "font-display text-xl font-bold tracking-tight"
                    }`}
                  >
                    {translation.out}
                  </p>
                  <p dir="auto" className="mt-2 whitespace-pre-wrap text-sm text-ink-soft">
                    {translation.source_text}
                  </p>
                </div>
              ))}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {translation.out.length <= 400 && (
                  <>
                <button
                  data-testid="translation-speak"
                  onClick={() => speak(translation.out, false, { translation: translation.source_text, language: translation.target })}
                  className="rounded-full border border-white/10 p-2 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                  aria-label="Hear the translation"
                >
                  <Volume2 className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid="translation-slow"
                  onClick={() => speak(translation.out, true, { translation: translation.source_text, language: translation.target })}
                  className="rounded-full border border-white/10 px-2.5 py-1.5 text-[10px] font-bold tracking-wide text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                >
                  {playing === `slow:${translation.out}` ? "…" : "SLOW"}
                </button>
                  </>
                )}
                <button
                  data-testid="edit-translation-button"
                  onClick={() => (editing ? finishEditing() : setEditing(true))}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    editing
                      ? "border-brand bg-brand/15 text-brand"
                      : "border-white/10 text-ink-soft hover:border-brand/50 hover:text-brand"
                  }`}
                >
                  <Pencil className="h-3.5 w-3.5" /> {editing ? "Done" : "Edit"}
                </button>
                <button
                  data-testid="translation-copy"
                  onClick={() => copyPhrase({ text: translation.out, translation: translation.source_text })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
                <button
                  data-testid="translation-share"
                  onClick={() =>
                    sharePhrase({
                      text: translation.out,
                      translation: translation.source_text,
                      language: translation.target,
                    })
                  }
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                >
                  <Share2 className="h-3.5 w-3.5" /> Share
                </button>
                <button
                  data-testid="download-docx"
                  onClick={() => downloadTranslation("docx")}
                  disabled={exporting === "docx"}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                >
                  <FileDown className="h-3.5 w-3.5" /> {exporting === "docx" ? "Building…" : "Word"}
                </button>
                <button
                  data-testid="download-pdf"
                  onClick={() => downloadTranslation("pdf")}
                  disabled={exporting === "pdf"}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                >
                  <FileDown className="h-3.5 w-3.5" /> {exporting === "pdf" ? "Building…" : "PDF"}
                </button>
                <button
                  data-testid="save-letter-button"
                  onClick={saveLetter}
                  disabled={savingLetter}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    activeLetter
                      ? "border-brand bg-brand/15 text-brand"
                      : "border-white/10 text-ink-soft hover:border-brand/50 hover:text-brand"
                  }`}
                >
                  {savingLetter ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FolderPlus className="h-3.5 w-3.5" />
                  )}
                  {activeLetter ? "In my letters" : "Save to my letters"}
                </button>
                <span
                  data-testid="translation-kept-note"
                  className="ms-auto inline-flex items-center gap-1.5 text-xs text-forest"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Kept under Translations
                </span>
              </div>
            </div>
          )}

          <LetterFolder
            languages={languages}
            refreshKey={letterRefresh}
            activeId={activeLetter}
            onOpen={openLetter}
            onDeleted={() => setActiveLetter(null)}
          />

          <div className="mt-8 border-t border-white/10 pt-7" data-testid="survival-section">
            <p className="inline-flex items-center gap-2 font-display text-lg font-bold tracking-tight">
              <LifeBuoy className="h-4 w-4 text-brand" /> Survival Phrases
            </p>
            <p className="mt-1 text-sm text-ink-soft">
              No grammar, no lessons — the lines you can use in five seconds. Pick a language and take the ones
              you need.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <LanguageSelect
                languages={languages}
                value={target}
                onChange={(code) => {
                  setTarget(code);
                  setPacks([]);
                  loadWeekPack(code);
                  loadPackProgress(code);
                }}
                testId="survival-language-select"
              />
              <Button
                data-testid="survival-load-button"
                onClick={refreshList}
                disabled={packBusy === "load"}
                className="min-h-[44px] rounded-full bg-brand px-5 hover:bg-brand-hover active:scale-95"
              >
                {packBusy === "load" && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {packs.length ? "Refresh list" : "Show me the phrases"}
              </Button>
              {packs.length > 0 && (
                <span className="inline-flex items-center gap-3 text-xs text-ink-soft">
                  <span data-testid="survival-set-label">
                    Set {variant + 1} of {variantCount}
                  </span>
                  {hiddenCount > 0 && (
                    <button
                      data-testid="restore-hidden-button"
                      onClick={restoreHidden}
                      className="underline decoration-dotted hover:text-ink"
                    >
                      Show {hiddenCount} hidden
                    </button>
                  )}
                </span>
              )}
            </div>

            {orderedPacks.map((pack) => (
              <div
                key={pack.id}
                data-testid={`survival-pack-${pack.id}`}
                className="mt-4 rounded-2xl border border-white/10 bg-page p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    data-testid={`survival-toggle-${pack.id}`}
                    onClick={() => setOpenPack(openPack === pack.id ? null : pack.id)}
                    className="text-start"
                  >
                    <span className="block font-display text-sm font-bold tracking-tight">
                      {pack.title} · {pack.phrases.length}
                    </span>
                    <span className="block text-xs text-ink-soft">{pack.blurb}</span>
                  </button>
                  <span className="flex flex-wrap items-center gap-2">
                  <span data-testid={`pack-mastery-${pack.id}`}>
                    <MasteryRing {...packMastery(pack)} />
                  </span>
                  <button
                    data-testid={`survival-pin-${pack.id}`}
                    onClick={() => togglePin(pack)}
                    title={pinned.includes(pack.id) ? "Unpin this pack" : "Pin this pack to the top"}
                    className={`rounded-full border p-1.5 transition-colors ${
                      pinned.includes(pack.id)
                        ? "border-brand/60 text-brand"
                        : "border-white/10 text-ink-soft hover:text-ink"
                    }`}
                  >
                    <Pin className="h-3.5 w-3.5" />
                  </button>
                  <Button
                    data-testid={`survival-drill-${pack.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => startDrill(pack)}
                    className="min-h-[40px] rounded-full px-4"
                  >
                    Quiz me
                  </Button>
                  <Button
                    data-testid={`survival-save-${pack.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => savePack(pack)}
                    disabled={packBusy === pack.id}
                    className="min-h-[40px] rounded-full px-4"
                  >
                    {packBusy === pack.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" />
                    )}
                    <span className="ms-1.5">Save all</span>
                  </Button>
                  <Button
                    data-testid={`survival-share-${pack.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => sharePack(pack)}
                    className="min-h-[40px] rounded-full px-4"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    <span className="ms-1.5">Share</span>
                  </Button>
                  </span>
                </div>

                {drill?.packId === pack.id && (
                  <div className="mt-3 rounded-2xl border border-brand/30 bg-panel p-4" data-testid={`drill-${pack.id}`}>
                    <p className="text-xs text-ink-soft">
                      {drill.at + 1} of {drill.queue.length} · {drill.right} right
                    </p>
                    <p className="mt-1 font-display text-sm font-bold tracking-tight" data-testid="drill-prompt">
                      How do you say “{drill.queue[drill.at].en}”?
                    </p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {drillOptions(pack, drill.queue[drill.at]).map((opt) => (
                        <button
                          key={opt.text}
                          data-testid={`drill-option-${opt.en.slice(0, 10)}`}
                          onClick={() => !drill.picked && answerDrill(pack, opt)}
                          dir="auto"
                          className={`min-h-[44px] rounded-xl border px-3 text-start text-sm transition-colors ${
                            !drill.picked
                              ? "border-white/10 hover:border-brand/50"
                              : opt.text === drill.queue[drill.at].text
                              ? "border-forest bg-forest/15"
                              : opt.text === drill.picked
                              ? "border-destructive bg-destructive/10"
                              : "border-white/10 opacity-60"
                          }`}
                        >
                          {opt.text}
                        </button>
                      ))}
                    </div>
                    <button
                      data-testid={`drill-stop-${pack.id}`}
                      onClick={() => setDrill(null)}
                      className="mt-3 text-xs text-ink-soft underline decoration-dotted hover:text-ink"
                    >
                      Stop the quiz
                    </button>
                  </div>
                )}
                {openPack === pack.id && (
                  <ul className="mt-3 space-y-1.5">
                    {pack.phrases.map((ph) => (
                      <li
                        key={ph.en}
                        className="flex items-start justify-between gap-3 rounded-xl px-2 py-1.5 text-sm hover:bg-panel"
                      >
                        <span className="min-w-0">
                          <span dir="auto" className="block font-medium">
                            <span
                              data-testid={`phrase-state-${pack.id}-${ph.en.slice(0, 12)}`}
                              title={(STATE_STYLE[progress?.states?.[pkey(target, ph.text)]?.state || "new"]).label}
                              className={`me-2 inline-block h-2 w-2 rounded-full align-middle ${
                                (STATE_STYLE[progress?.states?.[pkey(target, ph.text)]?.state || "new"]).dot
                              }`}
                            />
                            {ph.text}
                          </span>
                          {ph.say && (
                            <span className="block text-xs italic text-brand/80">say: {ph.say}</span>
                          )}
                          <span className="block text-xs text-ink-soft">{ph.en}</span>
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5">
                        <button
                          data-testid={`survival-speak-${pack.id}-${ph.en.slice(0, 12)}`}
                          onClick={() => speak(ph.text, false, { translation: ph.en, phonetic: ph.say, language: target, pack_id: pack.id })}
                          title={`Hear "${ph.text}"`}
                          aria-label={`Hear ${ph.text} out loud`}
                          className={`rounded-full border border-white/10 p-1.5 transition-colors hover:border-brand/50 hover:text-ink ${
                            playing === ph.text ? "border-brand/60 text-brand" : "text-ink-soft"
                          }`}
                        >
                          {playing === ph.text ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Volume2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          data-testid={`survival-slow-${pack.id}-${ph.en.slice(0, 12)}`}
                          onClick={() => speak(ph.text, true, { translation: ph.en, phonetic: ph.say, language: target, pack_id: pack.id })}
                          title="Hear it slowly, syllable by syllable"
                          aria-label={`Hear ${ph.text} slowly`}
                          className={`rounded-full border border-white/10 px-2 py-1 text-[10px] font-semibold transition-colors hover:border-brand/50 hover:text-ink ${
                            playing === `slow:${ph.text}` ? "border-brand/60 text-brand" : "text-ink-soft"
                          }`}
                        >
                          {playing === `slow:${ph.text}` ? "…" : "SLOW"}
                        </button>
                        <button
                          data-testid={`survival-add-${pack.id}-${ph.en.slice(0, 12)}`}
                          onClick={() => add({ text: ph.text, language: target, translation: ph.en,
                                               source: "survival" })}
                          title="Save this phrase"
                          className="rounded-full border border-white/10 px-2.5 py-1 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-ink"
                        >
                          Save
                        </button>
                        <button
                          data-testid={`hide-phrase-${pack.id}-${ph.en.slice(0, 12)}`}
                          onClick={() => hidePhrase(ph)}
                          aria-label={`Remove ${ph.text} from my list`}
                          title="Remove this line from my list"
                          className="rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-destructive/60 hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <p className="mt-8 inline-flex items-center gap-2 font-display text-lg font-bold tracking-tight">
            <Sparkles className="h-4 w-4 text-forest" /> Suggest from my chats
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <LanguageSelect
              languages={languages}
              value={suggestLang}
              onChange={setSuggestLang}
              testId="suggest-language-select"
            />
            <Button
              data-testid="suggest-phrases-button" onClick={suggest} disabled={busy}
              className="min-h-[44px] rounded-full bg-forest px-5 text-page hover:bg-forest/90 active:scale-95"
            >
              {busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />} Suggest 10 phrases
            </Button>
            {suggestions.length > 0 && (
              <Button
                data-testid="save-all-suggestions-button"
                variant="outline" onClick={saveAllSuggestions} disabled={busy}
                className="min-h-[44px] rounded-full px-5 active:scale-95"
              >
                Save all {suggestions.length}
              </Button>
            )}
          </div>
          {suggestions.length > 0 && (
            <ul className="mt-5 space-y-2" data-testid="phrase-suggestions">
              {suggestions.map((s, i) => (
                <li
                  key={`${i}-${s.text}`}
                  className="flex items-start justify-between gap-3 rounded-2xl bg-page px-4 py-3 text-sm"
                >
                  <span>
                    <span dir="auto" className="block font-medium">{s.text}</span>
                    <span dir="auto" className="block text-xs text-ink-soft">{s.translation}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <button
                      data-testid={`speak-suggestion-${i}`}
                      onClick={() =>
                        speak(s.text, false, { translation: s.translation, language: suggestLang })
                      }
                      aria-label={`Hear ${s.text}`}
                      title="Hear it out loud"
                      className={`rounded-full border border-white/10 p-1.5 transition-colors hover:border-brand/50 hover:text-brand ${
                        playing === s.text ? "border-brand/60 text-brand" : "text-ink-soft"
                      }`}
                    >
                      <Volume2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`slow-suggestion-${i}`}
                      onClick={() =>
                        speak(s.text, true, { translation: s.translation, language: suggestLang })
                      }
                      aria-label={`Hear ${s.text} slowly`}
                      title="Hear it slowly, syllable by syllable"
                      className={`rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold tracking-wide transition-colors hover:border-brand/50 hover:text-brand ${
                        playing === `slow:${s.text}` ? "border-brand/60 text-brand" : "text-ink-soft"
                      }`}
                    >
                      {playing === `slow:${s.text}` ? "…" : "SLOW"}
                    </button>
                    <button
                      data-testid={`save-suggestion-${i}`}
                      onClick={() =>
                        add({ text: s.text, language: suggestLang, translation: s.translation, source: "ai" })
                      }
                      className="rounded-full bg-brand/20 px-3 py-1 text-xs text-brand hover:bg-brand/30"
                    >
                      Save
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <PhraseHistory
          history={history}
          playing={playing}
          onSpeak={(r, slow) =>
            speak(r.text, slow, {
              translation: r.translation,
              phonetic: r.phonetic,
              language: r.language,
              pack_id: r.pack_id,
            })
          }
          onForget={forgetPhrase}
          onCopy={copyPhrase}
          onShare={sharePhrase}
          onSave={(r) =>
            add({
              text: r.text,
              language: r.language,
              translation: r.translation,
              phonetic: r.phonetic || "",
              pack_id: r.pack_id || "",
              source: "history",
            })
          }
          onDelete={async (r) => {
            await api.delete(`/phrasebook/${r.phrase_id}`);
            loadHistory();
          }}
        />

      </div>

      <PageGuide name="tools" />
    </div>
  );
}
